<template>
    <div class="hidden lg:flex lg:flex-col lg:w-[477px] bg-black border-l border-[#2e2e2e] h-screen overflow-hidden">
        <!-- Top Bar -->
        <div class="px-3 py-3 bg-black flex items-center justify-between shrink-0 border-b border-white/5 gap-2">
            <!-- Left: logo + links - z-[52] so above KebabMenu backdrop and not blurred -->
            <div class="relative flex items-center gap-2 min-w-0" :class="[ requiresAgeVerification ? 'z-[49]' : 'z-[52]' ]">
                <img
                    src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png"
                    alt="Logo"
                    class="h-[2rem] xl:h-[2.5rem] object-contain shrink-0"
                />

                <Link
                    :href="getMainAuditionsUrl(isUserLoggedIn, routePrefix)"
                    class="border border-blue-500/50 bg-blue-500/10 text-blue-400 px-3 xl:px-4 py-1.5 rounded-lg text-[0.6rem] xl:text-[0.65rem] font-semibold transition-all hover:bg-blue-500/20 hover:border-blue-500/70 whitespace-nowrap"
                >
                    AUDITIONS
                </Link>

                <a v-if="!requiresAgeVerification" :href="isUserLoggedIn ? route('members2.main.home') : route('www.main.home')"
                    class="border border-white/15 bg-transparent text-white/60 px-3 xl:px-4 py-1.5 rounded-lg text-[0.6rem] xl:text-[0.65rem] font-medium transition-all hover:border-white/25 hover:bg-white/5 hover:text-white/80 whitespace-nowrap"
                >
                    VIDEOS
                </a>

                <Link
                    :href="getCreateCastingUrl(isUserLoggedIn, routePrefix)"
                    @click="handleCreateCastingClick"
                    class="relative border border-amber-500/50 bg-amber-500/10 text-amber-400 px-3 xl:px-4 py-1.5 rounded-lg text-[0.6rem] xl:text-[0.65rem] font-semibold transition-all hover:bg-amber-500/20 hover:border-amber-500/70 whitespace-nowrap"
                >
                    CREATE
                    <span class="absolute -top-1.5 -right-1.5 bg-amber-500 text-black text-[0.45rem] font-bold px-1 py-0.5 rounded-full leading-none">New</span>
                </Link>
            </div>
            <!-- Right: JOIN + KebabMenu - JOIN has z-[52] so above backdrop and not blurred; trigger is z-[52] in KebabMenu -->
            <div class="flex items-center gap-2 shrink-0">
                <span v-if="!isUserLoggedIn" class="relative" :class="[ requiresAgeVerification ? 'z-[49]' : 'z-[52]' ]">
                    <button
                        @click="handleGetStarsClick"
                        class="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-bold px-3 xl:px-4 py-1.5 xl:py-2 rounded-lg text-[0.6rem] xl:text-[0.65rem] transition-all shadow-lg shadow-blue-600/25 hover:shadow-xl hover:shadow-blue-600/30 whitespace-nowrap"
                    >
                        GET STARS
                    </button>
                </span>

                <KebabMenu
                    :isUserLoggedIn="isUserLoggedIn"
                    :isGoldUser="isGoldUser"
                    :starBalance="starBalance"
                    :requiresAgeVerification="requiresAgeVerification"
                    variant="compact"
                    :routePrefix="routePrefix"
                    @joinClick="$emit('joinClick')"
                    @goldJoinClick="$emit('goldJoinClick')"
                />
            </div>
        </div>

        <!-- Main Content Area - Scrollable -->
        <div class="flex-1 bg-[#141414] px-16 overflow-y-auto min-h-0 custom-scrollbar">
            <!-- Context Banner for Guests - Desktop Only (commented out) -->
            <!--
            <div v-if="!isUserLoggedIn" class="py-6">
                <div class="bg-gradient-to-br from-blue-600/10 to-blue-700/10 border border-blue-500/20 rounded-xl p-4">
                    <div class="flex items-start gap-2 mb-3">
                        <Info class="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
                        <div>
                            <h4 class="text-white font-bold text-sm mb-1">
                                About Auditions
                            </h4>
                            <p class="text-white/70 text-xs leading-relaxed">
                                Members sponsor auditions with monthly Stars to help decide what fantasies get released.
                            </p>
                        </div>
                    </div>

                    <div class="space-y-1.5 mb-3">
                        <div class="flex items-center gap-2 text-xs">
                            <div class="w-1 h-1 rounded-full bg-blue-400" />
                            <span class="text-white/80">
                                14,000+ full videos included
                            </span>
                        </div>
                        <div class="flex items-center gap-2 text-xs">
                            <div class="w-1 h-1 rounded-full bg-blue-400" />
                            <span class="text-white/80">
                                Monthly Stars to sponsor
                            </span>
                        </div>
                    </div>

                    <button
                        @click="$emit('joinClick')"
                        class="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs rounded-lg transition-colors"
                    >
                        Join to Sponsor
                    </button>
                </div>
            </div>
            -->

            <!-- Sponsorship Widget -->
            <div class="py-6">
                <!-- Circle Progress Ring + Stats -->
                <div v-if="videoData.feed_post_status != 3" class="flex items-center gap-4 mb-5">
                    <!-- Circular Progress Ring -->
                    <div class="relative shrink-0 w-[76px] h-[76px]">
                        <svg class="w-full h-full -rotate-90" viewBox="0 0 84 84">
                            <!-- Track -->
                            <circle
                                cx="42" cy="42" :r="RING_RADIUS"
                                fill="none"
                                stroke="rgba(255,255,255,0.08)"
                                stroke-width="6"
                            />
                            <!-- Progress arc -->
                            <circle
                                cx="42" cy="42" :r="RING_RADIUS"
                                fill="none"
                                stroke="url(#progressGradient)"
                                stroke-width="6"
                                stroke-linecap="round"
                                :stroke-dasharray="RING_CIRCUMFERENCE"
                                :stroke-dashoffset="progressOffset"
                                class="transition-all duration-700 ease-out"
                            />
                            <defs>
                                <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stop-color="#2563eb" />
                                    <stop offset="100%" stop-color="#3b82f6" />
                                </linearGradient>
                            </defs>
                        </svg>
                        <!-- Percentage text in center -->
                        <div class="absolute inset-0 flex items-center justify-center">
                            <span class="text-white/90 text-xs font-bold">{{ sponsorPercentage }}%</span>
                        </div>
                    </div>

                    <!-- Stats text -->
                    <div>
                        <h3 class="text-white text-lg font-bold leading-tight">
                            {{ videoData.likes || 0 }} Stars raised
                        </h3>
                        <p class="text-gray-400 text-sm">
                            of {{ formatNumber(videoData.star_goal || 0) }} goal
                        </p>
                    </div>
                </div>

                <!-- Give Stars / Sponsor Button -->
                <button
                    v-if="isUserLoggedIn"
                    @click="handleGiveStarsClick"
                    class="w-full py-3 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-bold rounded-lg transition-all shadow-lg shadow-blue-600/20 hover:shadow-xl hover:shadow-blue-600/30"
                >
                    Give Stars
                </button>
                <button
                    v-else
                    @click="handleGetStarsClick"
                    class="w-full py-3 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-bold rounded-lg transition-all shadow-lg shadow-blue-600/20 hover:shadow-xl hover:shadow-blue-600/30"
                >
                    Get Stars to Sponsor
                </button>

                <!-- Gold Member Bonuses — visually separated section that sits under the
                     primary Give Stars CTA. The divider + heading frame the gold-tinted
                     pill(s) as a distinct "bonus" group rather than a single stray button,
                     so we have a clear hook to add additional bonus rows here later
                     (gifting boosts, exclusive auditions, etc.). Only rendered for
                     logged-in non-gold members. -->
                <!-- NOTE: Plus Gold 10X bonus banner temporarily DISABLED.
                     Uncomment to re-enable the 10X upsell entry point.
                <div v-if="isUserLoggedIn && !hasPlusGold" class="mt-5 pt-5 border-t border-white/10">
                    <button
                        @click="handleTenxBonusClick"
                        type="button"
                        class="gold-bonus-banner"
                        :aria-label="tenxCopy
                            ? `Upgrade to Gold — your ${tenxCopy.balance} ${tenxCopy.starWord} ${tenxCopy.verb} ${tenxCopy.boosted}`
                            : 'Upgrade to Gold — every star you give counts as 10'"
                    >
                        <span class="gold-bonus-banner__shimmer" aria-hidden="true"></span>

                        <span class="gold-bonus-banner__badge">
                            <span class="gold-bonus-banner__badge-label">GOLD BONUS</span>
                            <span class="gold-bonus-banner__badge-multiplier">10&times;</span>
                        </span>

                        <span class="gold-bonus-banner__copy">
                            <span class="gold-bonus-banner__heading">10X Your Stars with Gold</span>
                            <span v-if="tenxCopy" class="gold-bonus-banner__subline">
                                Your <strong>{{ tenxCopy.balance }}</strong>
                                {{ tenxCopy.starWord }}
                                {{ tenxCopy.verb }}
                                <strong>{{ tenxCopy.boosted }}</strong>
                            </span>
                            <span v-else class="gold-bonus-banner__subline">
                                Every star you give counts as <strong>10</strong>
                            </span>
                        </span>
                    </button>
                </div>
                -->
                <!-- /Plus Gold 10X bonus banner -->
            </div>

            <!-- Sponsors List -->
            <div v-if="videoData.feed_post_status != 3" class="py-5 border-t border-white/10">
                <!-- Section heading -->
                <h4 class="text-white font-bold text-sm mb-3">
                    Sponsors{{ sponsorCount > 0 ? ` (${sponsorCount})` : '' }}
                </h4>

                <!-- Empty state -->
                <p v-if="sidebarSponsors.length === 0" class="text-white/50 text-sm py-3">
                    Be the first to sponsor this audition!
                </p>

                <div v-else class="space-y-0">
                    <div
                        v-for="(sponsor, index) in sidebarSponsors"
                        :key="index"
                        class="flex items-center gap-3 py-3 cursor-pointer"
                        :class="{ 'border-t border-white/5': index > 0 }"
                        @click="goToMyProfile(sponsor)"
                    >
                        <!-- Avatar -->
                        <div class="w-9 h-9 rounded-full shrink-0 flex items-center justify-center"
                            :class="index === 0 ? 'bg-gradient-to-br from-amber-400 to-amber-600' : 'bg-zinc-700'"
                        >
                            <span class="text-white text-sm font-semibold">{{ getSponsorInitial(sponsor) }}</span>
                        </div>

                        <!-- Info -->
                        <div class="min-w-0 flex-1">
                            <p class="text-white text-sm font-medium truncate">{{ sponsor.name }}</p>
                            <div class="flex items-center gap-1.5">
                                <span class="text-gray-400 text-xs font-semibold">{{ sponsor.amount }} Stars</span>
                                <span v-if="sponsor.label" class="text-gray-500 text-xs">
                                    &middot; {{ sponsor.label }}
                                </span>
                            </div>
                        </div>

                        <!-- Star amount (right aligned) -->
                        <div class="flex items-center gap-1 shrink-0">
                            <span class="text-white font-bold text-sm">{{ sponsor.amount }}</span>
                            <img src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png" alt="" class="w-4 h-4" />
                        </div>
                    </div>
                </div>

                <!-- See all / See top buttons -->
                <div class="flex gap-3 mt-4">
                    <button @click="handleSeeCommentsClick" class="flex-1 py-2.5 border border-white/15 text-white/70 text-sm font-medium rounded-lg hover:bg-white/5 hover:border-white/25 transition-all">
                        See comments
                    </button>
                    <button @click="handleSeeTopClick" class="flex-1 py-2.5 border border-white/15 text-white/70 text-sm font-medium rounded-lg hover:bg-white/5 hover:border-white/25 transition-all inline-flex items-center justify-center gap-1.5">
                        <img src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png" alt="" class="w-3.5 h-3.5" />
                        See top
                    </button>
                </div>
            </div>

            <!-- Related Auditions Grid -->
            <div v-if="moreAuditions.length > 0" class="py-6 border-t border-white/10">
                <h3 class="text-white font-bold text-sm mb-4">
                    More Auditions
                </h3>
                <div class="grid grid-cols-2 gap-3">
                    <Link
                        v-for="audition in moreAuditions"
                        :key="audition.id"
                        :href="isUserLoggedIn ?
                            route('members2.reels.feed', {
                                tag: 'auditions',
                                post: audition.slug
                            }) :
                            route(routePrefix + '.reels.feed', {
                                tag: 'auditions',
                                post: audition.slug
                            })"
                        class="group relative bg-zinc-900 rounded-lg overflow-hidden hover:ring-2 hover:ring-white/20 transition-all text-left"
                    >
                        <!-- Thumbnail -->
                        <div class="relative w-full aspect-[3/4] bg-zinc-800">
                            <img v-if="requiresAgeVerification"
                                :src="audition.lock.start_card_video_images.image_600x1066_webp"
                                :alt="audition.title"
                                class="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-300"
                            />
                            <img v-else
                                :src="audition.start_card_images?.image_600x1066_webp"
                                :alt="audition.title"
                                class="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-300"
                            />

                            <!-- Status Badge -->
                            <div v-if="audition.is_near_goal" class="absolute top-2 left-2">
                                <div class="inline-flex items-center gap-1.5 px-2 py-1 rounded-md min-h-[22px] text-[10px] leading-none font-semibold tracking-wide uppercase bg-black/55 backdrop-blur-md border border-amber-400/30 text-amber-200 shadow-sm">
                                    <TrendingUp class="w-3 h-3 opacity-90" />
                                    <span>Near Goal</span>
                                </div>
                            </div>

                            <!-- Progress Bar Overlay -->
                            <div v-if="(audition.is_new || audition.is_near_goal)" class="absolute bottom-0 left-0 right-0 px-2 pb-2">
                                <div class="relative h-1.5 bg-white/8 rounded-full overflow-hidden">
                                    <div
                                        class="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-600 to-blue-500 rounded-full transition-all duration-300"
                                        :style="{ width: `${Math.min(getPercentage(audition), 100)}%` }"
                                    />
                                </div>
                            </div>
                        </div>

                        <!-- Info -->
                        <div class="p-2">
                            <h4 class="text-white text-xs font-semibold line-clamp-2 leading-tight mb-1">
                                {{ audition.title }}
                            </h4>
                            <p class="text-gray-400 text-[12px] line-clamp-1">
                                {{ audition.creator.name }}
                            </p>
                        </div>
                    </Link>
                </div>
            </div>

            <!-- Footer Links -->
            <div class="py-6 mt-4 text-[11px] text-gray-500 space-y-2 border-t border-white/10">
                <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <a href="https://support.latouraineinc.com" class="text-[#6d6d6d] hover:text-gray-300" target="_blank" rel="noopener">Support</a>
                    <span class="text-[#6d6d6d]">|</span>
                    <Link :href="route('www.main.privacyPolicy')" class="text-[#6d6d6d] hover:text-gray-300" target="_blank" rel="noopener">Privacy Policy</Link>
                    <span class="text-[#6d6d6d]">|</span>
                    <Link :href="route('www.main.termsOfService')" class="text-[#6d6d6d] hover:text-gray-300" target="_blank" rel="noopener">Terms of Service</Link>
                </div>
                <div class="flex flex-wrap items-center gap-x-1 gap-y-1">
                    <a href="https://www.epoch.com" class="text-[#6d6d6d] hover:text-gray-300" target="_blank" rel="noopener">Epoch.com</a>
                    <span class="text-[#6d6d6d]">|</span>
                    <a href="https://segpaycs.com" class="text-[#6d6d6d] hover:text-gray-300" target="_blank" rel="noopener">Segpay.com</a>
                </div>
                <div class="flex flex-wrap items-center gap-x-1 gap-y-1">
                    <Link :href="route('www.main.usc2257')" class="text-[#6d6d6d] hover:text-gray-300" target="_blank" rel="noopener">18 USC 2257 Record-Keeping Requirements Compliance Statement</Link>
                    <span class="text-[#6d6d6d]">|</span>
                    <Link :href="route('www.main.complianceMastercard')" class="text-[#6d6d6d] hover:text-gray-300" target="_blank" rel="noopener">Compliance with MasterCard Standards</Link>
                </div>
                <p class="text-[10px] text-[#8a8a8a]">Digital Experience Corporation (Registration 4804852) is an authorized payment agent.</p>
                <div class="text-[10px] text-[#8a8a8a]">
                    <p class="font-bold">KALOSTA LTD (HE 488596) is an approved processing agent</p>
                    <p>Naxou 1</p>
                    <p>Floor 1, Office 103</p>
                    <p>2043 Nicosia, Cyprus</p>
                    <p>TAX ID: 603331300</p>
                </div>
                <p class="text-[10px]">&copy; {{ new Date().getFullYear() }} La Touraine, Inc.</p>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue'
import { Info, TrendingUp } from 'lucide-vue-next'
import KebabMenu from './KebabMenu.vue'
import {Link, router} from "@inertiajs/vue3";
import {getCreateCastingUrl, getMainAuditionsUrl} from "@/Helpers/auditions";
import {trackFathomEvent, getFathomPostPrefix, getFathomPagePrefix} from "@/Helpers/analytics";

const props = defineProps({
    videoData: {
        type: Object,
        default: () => ({})
    },
    moreAuditions: {
        type: Array,
    },
    isUserLoggedIn: {
        type: Boolean,
        default: false
    },
    isGoldUser: {
        type: Boolean,
        default: false
    },
    // True only for one-time Plus Gold buyers. CTA visibility for the 10X
    // upsell gates on the inverse so recurring NA Gold subscribers (who
    // don't have the 10x bonus) still see the one-time upgrade pitch.
    hasPlusGold: {
        type: Boolean,
        default: false
    },
    routePrefix: {
        type: String
    },
    starBalance: {
        type: Number,
        default: 0
    },
    sponsorsData: {
        type: Object,
        default: () => ({ data: [], total: 0, current_page: 1, last_page: 1 })
    },
    requiresAgeVerification: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['joinClick', 'starClick', 'goldJoinClick', 'viewSponsors', 'tenxBonusClick'])

const fathomPrefix = computed(() => getFathomPostPrefix(props.routePrefix))

const handleGiveStarsClick = () => {
    trackFathomEvent(`${fathomPrefix.value}_Sidebar_Give_Stars_Clicked`)
    emit('starClick')
}

const handleGetStarsClick = () => {
    trackFathomEvent(`${fathomPrefix.value}_Sidebar_Get_Stars_Clicked`)
    emit('joinClick')
}

const handleSeeCommentsClick = () => {
    trackFathomEvent(`${fathomPrefix.value}_Sidebar_See_Comments_Clicked`)
    emit('viewSponsors')
}

const handleSeeTopClick = () => {
    trackFathomEvent(`${fathomPrefix.value}_Sidebar_See_Top_Clicked`)
    emit('viewSponsors')
}

const handleTenxBonusClick = () => {
    trackFathomEvent(`${fathomPrefix.value}_Sidebar_Tenx_Bonus_Clicked`)
    emit('tenxBonusClick')
}

// Personalized 10× math for the gold-bonus banner. Returns null when the
// user has no stars so the template can render a generic pitch instead of
// the dead "Your 0 stars become 0" line.
const tenxCopy = computed(() => {
    const balance = props.starBalance || 0
    if (balance <= 0) {
        return null
    }
    return {
        balance: balance.toLocaleString(),
        starWord: balance === 1 ? 'star' : 'stars',
        verb: balance === 1 ? 'becomes' : 'become',
        boosted: (balance * 10).toLocaleString(),
    }
})

const creatorInitial = computed(() => props.videoData.creator.name?.[0] || '?')

const getPercentage = (audition) => {
    return (audition.likes / audition.star_goal) * 100
}

const handleImageError = (e) => {
    e.target.style.display = 'none'
    e.target.parentElement.innerHTML = `<div class="w-full h-full flex items-center justify-center text-white text-xl font-bold">${creatorInitial.value}</div>`
}

// --- Sponsorship widget ---
const sponsorPercentage = computed(() => {
    const likes = props.videoData.likes || 0
    const goal = props.videoData.star_goal || 1
    const percentage = Math.round((likes / goal) * 100)
    return percentage > 100 ? 100 : percentage
})

// Derive sidebar sponsor list from the API data
const sidebarSponsors = computed(() => {
    return (props.sponsorsData.data || []).slice(0, 5).map((s, index) => ({
        name: s.sponsor_name,
        amount: s.total_stars,
        label: index === 0 ? 'Top sponsor' : (index === 1 ? 'Recent sponsor' : null),
    }))
})
const sponsorCount = computed(() => props.sponsorsData.total || props.videoData?.unique_sponsors_count || 0)

// SVG progress ring calculations
const RING_RADIUS = 38
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS // ~238.76

const progressOffset = computed(() => {
    return RING_CIRCUMFERENCE * (1 - sponsorPercentage.value / 100)
})

const formatNumber = (num) => {
    if (num >= 1000) {
        return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K'
    }
    return num.toLocaleString()
}

const getSponsorInitial = (sponsor) => {
    return sponsor.name?.[0] || '?'
}

const goToMyProfile = (entry) => {
    const url = props.isUserLoggedIn ?
        route('members2.profile.my', { slug: entry.name }) :
        route(props.routePrefix + '.profile.my', { slug: entry.name })

    router.visit(url, {
        preserveScroll: false,
        preserveState: true,
    })
}

const handleCreateCastingClick = () => {
    trackFathomEvent(`${fathomPrefix}_Top_Menu_Create_Casting_Button_Clicked`)
}
</script>
<style scoped>
.custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
    width: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.15);
    border-radius: 10px;
    transition: background-color 0.2s ease;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(255, 255, 255, 0.25);
}

.custom-scrollbar::-webkit-scrollbar-thumb:active {
    background-color: rgba(255, 255, 255, 0.35);
}

/* ===== Gold Bonus Banner — 10X Your Stars (Gold bonus) sidebar CTA =====
   Port of the gold-bonus-banner Blade component. All five interaction
   states preserved (base, hover, active, focus-visible, disabled). */
.gold-bonus-banner {
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border: none;
    border-radius: 12px;
    cursor: pointer;
    text-align: left;
    overflow: hidden;
    background: linear-gradient(160deg, #FFE27A 0%, #FFC23C 35%, #F59E0B 70%, #B8730A 100%);
    /* Resting state strips ALL outer glow that the source had — both the
       wide amber halo (`0 0 24px -4px ...`) and the soft amber drop-shadow
       (`0 8px 22px -6px ...`) — they read as too "lit up" sitting under
       the primary blue Give Stars CTA. Only inset highlights and the
       crisp 1px outer ring remain at rest. Hover restores both glows so
       interaction feedback is still obvious. */
    box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.55),
        inset 0 -2px 6px rgba(120, 60, 0, 0.3),
        0 0 0 1px rgba(255, 200, 80, 0.6);
    transition:
        transform 150ms ease-out,
        box-shadow 200ms ease-out,
        background 200ms ease-out;
}
.gold-bonus-banner:hover:not(:disabled) {
    transform: translateY(-1px);
    background: linear-gradient(160deg, #FFEC95 0%, #FFD04A 35%, #FBA610 70%, #C8800B 100%);
    /* Hover glow + drop-shadow dialed down from the source: tighter blur
       radii and lower alphas so the lift feels subtle instead of "the
       button just turned into a stadium light". The brighter gradient,
       1px translateY, and brightened outer ring still register the hover
       clearly without the heavy bloom. */
    box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.65),
        inset 0 -2px 6px rgba(120, 60, 0, 0.3),
        0 0 0 1px rgba(255, 215, 100, 0.85),
        0 0 14px -4px rgba(255, 200, 80, 0.3),
        0 6px 14px -6px rgba(245, 158, 11, 0.3);
}
.gold-bonus-banner:active:not(:disabled) {
    transform: scale(0.985);
    background: linear-gradient(160deg, #F5C950 0%, #E8A91A 35%, #C97A0E 70%, #8E5A07 100%);
    box-shadow:
        inset 0 2px 4px rgba(80, 40, 0, 0.3),
        0 0 0 1px rgba(255, 200, 80, 0.5),
        0 3px 10px -2px rgba(245, 158, 11, 0.4);
    transition-duration: 80ms;
}
.gold-bonus-banner:focus-visible {
    outline: 2px solid #FFD96B;
    outline-offset: 2px;
}
.gold-bonus-banner:disabled {
    cursor: not-allowed;
    opacity: 0.45;
    filter: saturate(0.6);
}
.gold-bonus-banner__shimmer {
    position: absolute;
    top: 0;
    left: 35%;
    width: 30%;
    height: 100%;
    background: linear-gradient(115deg, transparent 30%, rgba(255, 255, 255, 0.4) 50%, transparent 70%);
    transform: skewX(-20deg);
    pointer-events: none;
}
.gold-bonus-banner__badge {
    position: relative;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    padding: 7px 10px;
    border-radius: 10px;
    background: linear-gradient(180deg, #2A1A00 0%, #4A2D00 100%);
    color: #FFD96B;
    box-shadow:
        inset 0 1px 0 rgba(255, 200, 80, 0.25),
        0 1px 3px rgba(0, 0, 0, 0.3);
}
.gold-bonus-banner__badge-label {
    font-size: 8px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0.14em;
    opacity: 0.85;
}
.gold-bonus-banner__badge-multiplier {
    font-size: 16px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: -0.01em;
}
.gold-bonus-banner__copy {
    position: relative;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}
.gold-bonus-banner__heading {
    font-size: 13px;
    font-weight: 600;
    line-height: 1.2;
    color: #3D1F00;
}
.gold-bonus-banner__subline {
    font-size: 11px;
    line-height: 1.3;
    color: #6B3A00;
}
</style>
